import * as React from "react";
import { useContent } from "@thoughtbot/superglue";
import { ChevronLeftIcon, DownloadIcon, UsersIcon } from "lucide-react";

import { Form, type FormProps } from "@javascript/components/Inputs";
import { Badge } from "@javascript/components/ui/badge";
import { Button } from "@javascript/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@javascript/components/ui/empty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@javascript/components/ui/table";
import { TypographyH1, TypographyH2, TypographyMuted } from "@javascript/components/ui/typography";

type Respondent = {
  id: number;
  guestName: string | null;
  attendingLabel: string;
  partySizeLabel: string;
  message: string | null;
};

type AttendanceSummary = {
  responseCount: number;
  attendingCount: number;
  notAttendingCount: number;
  expectedHeadcount: number;
};

type GuestListExport = {
  id: number;
  state: "pending" | "ready" | "failed" | "released";
  stateLabel: string;
  requestedAt: string;
  requestedAtLabel: string;
  responseCount: number;
  responseCountLabel: string;
  downloadPath?: string;
};

type GuestsContent = {
  coupleNames: string;
  dashboardPath: string;
  summary: AttendanceSummary;
  respondents: Respondent[];
  guestListExports: GuestListExport[];
  requestGuestListExportForm?: FormProps<{ submit: { text: string } }>;
  exportRequestBlockedMessage?: string;
};

export default function Guests() {
  const {
    coupleNames,
    dashboardPath,
    summary,
    respondents,
    guestListExports,
    requestGuestListExportForm,
    exportRequestBlockedMessage,
  } = useContent<GuestsContent>();

  const supportingStats = [
    { label: "Responses", value: summary.responseCount },
    { label: "Attending", value: summary.attendingCount },
    { label: "Not attending", value: summary.notAttendingCount },
  ];

  return (
    <div className="p-6">
      <section className="mx-auto w-full max-w-3xl">
        <a
          href={dashboardPath}
          data-sg-visit="true"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeftIcon aria-hidden="true" className="size-4" />
          Back to dashboard
        </a>

        <TypographyH1 className="mt-4 text-left">{coupleNames}</TypographyH1>
        <TypographyMuted className="mt-2">Guests who responded to your invitation.</TypographyMuted>

        <div data-attendance-summary className="mt-6 rounded-lg border bg-background p-5 shadow-sm">
          <p className="text-sm font-medium text-muted-foreground">Expected guests</p>
          <p className="mt-1 font-serif text-5xl font-semibold tabular-nums tracking-tight">
            {summary.expectedHeadcount}
          </p>
          <div className="mt-4 grid grid-cols-3 gap-4 border-t pt-4">
            {supportingStats.map((stat) => (
              <div key={stat.label}>
                <p className="text-2xl font-semibold tabular-nums">{stat.value}</p>
                <p className="mt-0.5 text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        <section aria-labelledby="guest-list-exports-heading" className="mt-6 rounded-lg border bg-background p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <TypographyH2 id="guest-list-exports-heading" className="border-0 pb-0 text-2xl">
                Guest list exports
              </TypographyH2>
              <TypographyMuted className="mt-1">
                Download a CSV snapshot of the responses recorded so far.
              </TypographyMuted>
            </div>
            {requestGuestListExportForm ? (
              <Form
                {...requestGuestListExportForm.form}
                extras={requestGuestListExportForm.extras}
                data-sg-visit="true"
                className="shrink-0"
              >
                <Button type="submit" className="min-h-11 w-full sm:w-auto">
                  {requestGuestListExportForm.inputs.submit.text}
                </Button>
              </Form>
            ) : exportRequestBlockedMessage ? (
              <p data-export-request-blocked className="max-w-sm text-sm text-muted-foreground">
                {exportRequestBlockedMessage}
              </p>
            ) : null}
          </div>

          {guestListExports.length === 0 ? (
            <p data-export-empty className="mt-5 border-t pt-5 text-sm text-muted-foreground">
              No past exports yet.
            </p>
          ) : (
            <ul data-export-list className="mt-5 space-y-3 border-t pt-5">
              {guestListExports.map((guestListExport) => (
                <li
                  key={guestListExport.id}
                  data-export-row
                  className="flex min-w-0 flex-col gap-3 rounded-md border p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant={
                          guestListExport.state === "ready"
                            ? "success"
                            : guestListExport.state === "failed"
                              ? "destructive"
                              : "secondary"
                        }
                      >
                        {guestListExport.stateLabel}
                      </Badge>
                      <span className="text-sm font-medium">{guestListExport.responseCountLabel}</span>
                    </div>
                    <time
                      dateTime={guestListExport.requestedAt}
                      className="block text-sm text-muted-foreground"
                    >
                      Requested {guestListExport.requestedAtLabel}
                    </time>
                  </div>
                  {guestListExport.downloadPath ? (
                    <Button asChild variant="outline" className="min-h-11 w-full sm:w-auto">
                      <a href={guestListExport.downloadPath}>
                        <DownloadIcon aria-hidden="true" />
                        Download CSV
                      </a>
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        {respondents.length === 0 ? (
          <Empty className="mt-6 border border-solid bg-background">
            <EmptyHeader>
              <EmptyMedia variant="icon" aria-hidden="true">
                <UsersIcon />
              </EmptyMedia>
              <EmptyTitle>No responses yet</EmptyTitle>
              <EmptyDescription>
                No one has responded to this invitation yet. Responses appear here as soon as a
                guest RSVPs.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="mt-6 rounded-lg border bg-background shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[26%]">Guest</TableHead>
                  <TableHead className="w-[15%]">Attending</TableHead>
                  <TableHead className="w-[15%]">Party size</TableHead>
                  <TableHead>Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {respondents.map((respondent) => (
                  <TableRow key={respondent.id} data-respondent-row>
                    <TableCell className="min-w-0 font-medium">
                      {respondent.guestName ?? "Anonymous"}
                    </TableCell>
                    <TableCell>{respondent.attendingLabel}</TableCell>
                    <TableCell className="tabular-nums">{respondent.partySizeLabel}</TableCell>
                    <TableCell className="min-w-0 whitespace-normal break-words">
                      {respondent.message}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
}
